/**
 * 개발용 떠 있는 도구 — 화면 위에 떠 있고, 알약을 끌어 어디든 옮길 수 있다. `pnpm dev:mock`
 * 에서만 entry.ts 가 앱과 다른 React 루트로 띄우므로, 앱 코드는 이 도구를 모른다.
 *
 * 하는 일: mock 계정 전환.
 *
 * 계정을 전환하면 세션 쿠키를 바꾸고 홈(`/`)을 새로 읽는다. 실제로 다른 계정으로 다시
 * 로그인한 것과 같게 — 쿼리 캐시와 화면 상태가 이전 계정 것으로 남지 않는다.
 */
import { useEffect, useRef, useState, type PointerEvent } from "react";
import { Avatar } from "@/components/common/Avatar";
import { Badge } from "@/components/common/Badge";
import { ChevronDown, LogOut } from "@/components/icons";
import { cn } from "@/lib/cn";
import { ROLE_LABEL, ROLE_ORDER } from "@/lib/roles";
import { SESSION_CHANGED, db } from "./db";

const accounts = [...db.users].sort(
  (a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role)
);

/** 놓은 자리는 이 브라우저에만 기억한다 — 새로고침·계정 전환 뒤에도 그 자리에 뜨게. */
const POSITION_KEY = "mock:switcher-position";
/** 화면 가장자리와 띄우는 거리(px). */
const EDGE = 16;
/** 이만큼(px) 넘게 움직여야 드래그로 본다. 그 안이면 클릭이다. */
const DRAG_THRESHOLD = 4;

interface Position {
  x: number;
  y: number;
}

function loadPosition(): Position | null {
  try {
    const raw = localStorage.getItem(POSITION_KEY);
    return raw ? (JSON.parse(raw) as Position) : null;
  } catch {
    return null;
  }
}

function savePosition(position: Position) {
  try {
    localStorage.setItem(POSITION_KEY, JSON.stringify(position));
  } catch {
    // 기억하지 못해도 이번 페이지 안에서는 놓은 자리에 있다.
  }
}

/** 알약이 화면 밖으로 나가지 않게 가둔다. */
function clamp({ x, y }: Position, el: HTMLElement): Position {
  return {
    x: Math.min(Math.max(x, EDGE), window.innerWidth - el.offsetWidth - EDGE),
    y: Math.min(Math.max(y, EDGE), window.innerHeight - el.offsetHeight - EDGE),
  };
}

export default function DevFloatingTool() {
  const [me, setMe] = useState(() => db.currentUser());
  const [open, setOpen] = useState(false);
  /** null 이면 아직 옮긴 적 없음 — 우측 하단에 둔다. */
  const [position, setPosition] = useState<Position | null>(loadPosition);
  const pillRef = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ startX: number; startY: number; origin: Position; moved: boolean }>(
    undefined
  );

  useEffect(() => {
    const sync = () => setMe(db.currentUser());
    window.addEventListener(SESSION_CHANGED, sync);
    return () => window.removeEventListener(SESSION_CHANGED, sync);
  }, []);

  // 창 크기가 바뀌면(또는 기억한 자리가 지금 창 밖이면) 화면 안으로 다시 들인다.
  useEffect(() => {
    const refit = () => setPosition((p) => (p && pillRef.current ? clamp(p, pillRef.current) : p));
    refit();
    window.addEventListener("resize", refit);
    return () => window.removeEventListener("resize", refit);
  }, []);

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    drag.current = {
      startX: e.clientX,
      startY: e.clientY,
      origin: { x: rect.left, y: rect.top },
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    d.moved = true;
    setPosition(clamp({ x: d.origin.x + dx, y: d.origin.y + dy }, e.currentTarget));
  };

  const onPointerUp = (e: PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    drag.current = undefined;
    if (!d) return;
    if (d.moved) {
      const rect = e.currentTarget.getBoundingClientRect();
      savePosition({ x: rect.left, y: rect.top });
    } else {
      setOpen((v) => !v);
    }
  };

  const switchTo = (userId: string) => {
    if (userId === me?.id) return setOpen(false);
    db.signIn(userId);
    // 지금 주소가 새 계정에 권한 없는 페이지일 수 있다 — 홈에서 그 계정의 첫 화면으로 간다.
    location.assign("/");
  };

  // 메뉴는 화면 안쪽으로 연다 — 알약이 위쪽 절반이면 아래로, 왼쪽 절반이면 왼쪽 끝을 맞춰.
  const opensDown = position ? position.y < window.innerHeight / 2 : false;
  const alignsLeft = position ? position.x < window.innerWidth / 2 : false;

  return (
    <div
      // 앱의 어떤 층(사이드바·헤더·어시스턴트 패널·모달)보다 위에 뜬다.
      className={cn("fixed z-2147483647", !position && "right-4 bottom-4")}
      style={position ? { left: position.x, top: position.y } : undefined}
    >
      {open && (
        <div
          role="menu"
          className={cn(
            "absolute w-64 rounded-md bg-clay-surface p-1.5 shadow-lg ring ring-clay-border",
            opensDown ? "top-full mt-2" : "bottom-full mb-2",
            alignsLeft ? "left-0" : "right-0"
          )}
        >
          <p className="px-2 pt-1 pb-2 text-xs text-clay-text-minimal">계정 전환</p>
          {/* 계정이 많아도 메뉴가 화면 밖으로 넘치지 않게 목록만 스크롤한다. */}
          <div className="max-h-72 overflow-y-auto">
            {accounts.map((user) => (
              <button
                key={user.id}
                type="button"
                role="menuitemradio"
                aria-checked={user.id === me?.id}
                onClick={() => switchTo(user.id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-clay-action-primary-tonal",
                  user.id === me?.id && "bg-clay-action-primary-tonal"
                )}
              >
                <Avatar name={user.name} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-clay-text">
                    {user.name}
                  </span>
                  <span className="block truncate text-xs text-clay-text-sub">{user.email}</span>
                </span>
                <Badge>{ROLE_LABEL[user.role]}</Badge>
              </button>
            ))}
          </div>
          {me && (
            <a
              role="menuitem"
              href="/accounts/logout/"
              className="mt-1.5 flex h-8 items-center gap-2 rounded-sm border-t border-clay-divide-minimal px-2 pt-1.5 text-sm text-clay-text hover:bg-clay-action-primary-tonal"
            >
              <LogOut size={16} />
              로그아웃
            </a>
          )}
        </div>
      )}

      {/* 누르면 메뉴, 끌면 이동. 열고 닫기는 pointerup 에서 판정한다 — click 으로 받으면
          끌어 놓은 직후에도 메뉴가 열린다. 키보드(Enter·Space)는 click 으로 들어온다. */}
      <button
        ref={pillRef}
        type="button"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => (drag.current = undefined)}
        onClick={(e) => e.detail === 0 && setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="끌어서 옮길 수 있습니다"
        className="flex h-9 cursor-grab touch-none items-center gap-2 rounded-full bg-clay-surface-inverse px-3 text-sm whitespace-nowrap text-clay-text-inverse shadow-lg select-none hover:bg-clay-action-primary-hover active:cursor-grabbing"
      >
        {/* 진한 버튼 위라 옅은 배지는 묻힌다 — 채운 바탕으로 쓴다. */}
        <Badge className="bg-clay-surface-highlight-primary text-clay-text-inverse">DEV</Badge>
        {me && <Avatar name={me.name} size="xs" />}
        <span className="font-medium">
          {me ? `${me.name} · ${ROLE_LABEL[me.role]}` : "로그아웃 상태"}
        </span>
        <ChevronDown
          size={14}
          className={cn("transition-transform", open === opensDown && "rotate-180")}
        />
      </button>
    </div>
  );
}
